import { useMemo } from 'react';
import { CustomFieldType } from 'src/api/generated';
import { useCustomFieldDefinitions } from 'src/modules/shared/hooks/api/custom-fields/definitions';
import { CustomFieldEntityType } from 'src/api/generated';
import { CUSTOM_FIELD_TYPE_PREFIX } from 'src/modules/shared/utils/custom-fields.util';
import {
  type FilterFieldOptions,
  type SortFieldOptions,
  type StatusOption,
} from 'src/modules/shared/types/filters.types';
import { useSearch } from '@tanstack/react-router';
import { Route as CommunicationsRoute } from 'src/routes/communications';
import type { CommunicationsSearch } from 'src/modules/shared/schemas/communications-search.schema';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { resolveDateCustomFieldIncludeTime } from 'src/modules/shared/utils/custom-field-definition.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

function mapCustomFieldTypeToFilterType(
  type: CustomFieldType
): FilterFieldOptions['type'] {
  switch (type) {
    case CustomFieldType.LIST:
      return 'list';
    case CustomFieldType.STRING:
      return 'string';
    case CustomFieldType.NUMBER:
      return 'number';
    case CustomFieldType.BOOLEAN:
      return 'boolean';
    case CustomFieldType.DATE:
      return 'date';
    case CustomFieldType.FILE:
      return 'string';
    default:
      return 'string';
  }
}

export interface UseConversationFieldsResult {
  filterFieldsOptions: FilterFieldOptions[];
  sortFieldsOptions: SortFieldOptions[];
}

/**
 * Builds the dynamic status date filter fields for conversations
 */
export const buildStatusDateFilterFields = (
  statusOptions?: StatusOption[]
): FilterFieldOptions[] => {
  if (!statusOptions || statusOptions.length === 0) return [];

  const fields: FilterFieldOptions[] = [
    {
      id: 'statusDateFields.date_lastStatusChangeAt',
      // TODO: check when implementing i18n
      label: '🕒 Última transición',
      type: 'date',
      includeTime: true,
    },
  ];

  statusOptions.forEach((status) => {
    fields.push({
      id: `statusDateFields.date_status_${status.id}_lastChangedAt`,
      // TODO: check when implementing i18n
      label: `🕒 Última transición a: ${status.name}`,
      type: 'date',
      includeTime: true,
    });
  });

  return fields;
};

export function useConversationFields(params?: {
  statusOptions?: StatusOption[];
}): UseConversationFieldsResult {
  const { t } = useI18n();
  const { funnelId } = useSearch({
    from: CommunicationsRoute.id,
    select: (search: CommunicationsSearch) => ({
      funnelId: search.funnelId,
    }),
  });

  const funnelIdQuery = useMemo(() => {
    if (!funnelId) return {};

    return createQuery()
      .equals('funnels.some.funnelId', funnelId)
      .take(100)
      .build();
  }, [funnelId]);

  const { definitions: conversationDefinitions } = useCustomFieldDefinitions({
    entityType: CustomFieldEntityType.FUNNEL_CONVERSATION,
    ...funnelIdQuery,
  });

  const { definitions: contactDefinitions } = useCustomFieldDefinitions({
    entityType: CustomFieldEntityType.CONTACT,
    ...funnelIdQuery,
  });

  const conversationCustomFieldsOptions = useMemo(() => {
    return conversationDefinitions.map((def) => ({
      id: `customFields.${CUSTOM_FIELD_TYPE_PREFIX[def.fieldType]}${def.fieldKey}`,
      label: `💬 ${def.fieldLabel ?? def.fieldKey}`,
      type: mapCustomFieldTypeToFilterType(def.fieldType),
      definitionId: def.id,
      includeTime: resolveDateCustomFieldIncludeTime(
        def.fieldType,
        def.includeTime
      ),
    }));
  }, [conversationDefinitions]);

  const contactCustomFieldsOptions = useMemo(() => {
    return contactDefinitions.map((def) => ({
      id: `contacts.customFields.${CUSTOM_FIELD_TYPE_PREFIX[def.fieldType]}${def.fieldKey}`,
      label: `👤 ${def.fieldLabel ?? def.fieldKey}`,
      type: mapCustomFieldTypeToFilterType(def.fieldType),
      definitionId: def.id,
      includeTime: resolveDateCustomFieldIncludeTime(
        def.fieldType,
        def.includeTime
      ),
    }));
  }, [contactDefinitions]);

  const filterFieldsOptions: FilterFieldOptions[] = useMemo(() => {
    const statusDateFields = buildStatusDateFilterFields(params?.statusOptions);

    return [
      // TODO: check when implementing i18n
      {
        id: 'userId',
        label: 'Asignado',
        type: 'user',
      },
      {
        id: 'propertyId',
        label: t('communications.filters.propertyOfInterest'),
        type: 'property',
      },
      {
        id: 'funnelStatusId',
        label: 'Estado',
        type: 'status',
      },
      {
        id: 'archivedStatus',
        label: 'Archivación',
        type: 'list',
      },
      {
        id: 'contacts.firstName',
        label: 'Nombre de contacto',
        type: 'string',
      },
      {
        id: 'contacts.lastName',
        label: 'Apellido de contacto',
        type: 'string',
      },
      {
        id: 'contacts.addresses.address',
        label: 'Dirección de contacto',
        type: 'string',
      },
      {
        id: 'lastInteractionAt',
        label: '🕒 Última interacción',
        type: 'date',
        includeTime: true,
      },
      {
        id: 'lastInteraction.direction',
        label: 'Dirección de la última interacción',
        type: 'list',
      },
      ...conversationCustomFieldsOptions,
      ...contactCustomFieldsOptions,
      ...statusDateFields,
      {
        id: 'createdAt',
        label: '🕒 Creación',
        type: 'date',
        includeTime: true,
      },
      {
        id: 'updatedAt',
        label: '🕒 Actualización',
        type: 'date',
        includeTime: true,
      },
      {
        id: 'archivedAt',
        label: '🕒 Archivado',
        type: 'date',
        includeTime: true,
      },
    ];
  }, [
    conversationCustomFieldsOptions,
    contactCustomFieldsOptions,
    params?.statusOptions,
    t,
  ]);

  const sortFieldsOptions: SortFieldOptions[] = useMemo(() => {
    const baseSortFields: SortFieldOptions[] = filterFieldsOptions.filter(
      (field): field is SortFieldOptions =>
        field.type !== 'boolean' && field.type !== 'list'
    );

    const additionalSortOnlyFields: SortFieldOptions[] = [
      {
        id: 'lastInteraction.direction',
        // TODO: check when implementing i18n
        label: 'Dirección de la última interacción',
        type: 'string',
      },
    ];

    return [...baseSortFields, ...additionalSortOnlyFields];
  }, [filterFieldsOptions]);

  return {
    filterFieldsOptions,
    sortFieldsOptions,
  };
}
